import React, { useState, useEffect, useContext } from 'react';
import TitleBar from '../components/TitleBar.jsx';
import '../styles/release_notifications.css';
import { urlValue, loadingContext, unfolder, openAlert, useTranslate } from '../lib';
import axios from 'axios';
import { Alerts } from '../components/common/Alerts';
import DataTable from '../components/provider/datatable/Datatable';

const ReleaseNotifications = () => {
  const [loading, setLoading] = useContext(loadingContext);
  const [alert, setAlert] = useState({ title: '', visible: false });
  const [notifications, setNotifications] = useState([]);
  const [formData, setFormData] = useState({ title: '', description: '', date: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [editId, setEditId] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const { t } = useTranslate();
  
  // Límites de tamaño de archivo en bytes
  const MAX_FILE_SIZE = {
    'video/mp4': 20 * 1024 * 1024, // 20 MB para videos
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 10 * 1024 * 1024 // 10 MB para Word
  };
  
  const getNotifications = async () => {
    try {
      setLoading(true);
      const id = unfolder(localStorage.getItem('hashInfo'), 'IANestingKey').id;
      const token = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').token;
      const csrfToken = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').csrfToken;
      const res = await axios.get(`${urlValue}/studio/release-notification`, {
        headers: { 
          user: id, 
          'access-token': token,
          'X-CSRF-Token': csrfToken
        },
        withCredentials: true
      });
      setNotifications(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getNotifications();
  }, [refresh]);

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'video/mp4'];
      
      // Validar tipo de archivo
      if (!allowedTypes.includes(file.type)) {
        openAlert('#alert-ac', () => setAlert({ title: 'Only .docx and .mp4 files are allowed', visible: true }), 3000, () => setAlert({ visible: false }));
        e.target.value = ''; // Limpiar el input
        return;
      }
      
      // Validar tamaño de archivo
      const maxSize = MAX_FILE_SIZE[file.type];
      if (file.size > maxSize) {
        const maxSizeMB = Math.round(maxSize / (1024 * 1024));
        const fileType = file.type.includes('video') ? 'videos' : 'Word documents';
        openAlert('#alert-ac', () => setAlert({ 
          title: `File too large. Maximum size for ${fileType} is ${maxSizeMB} MB`, 
          visible: true 
        }), 4000, () => setAlert({ visible: false }));
        e.target.value = ''; // Limpiar el input
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const downloadFile = async (fileUrl, fileName) => {
    try {
      const id = unfolder(localStorage.getItem('hashInfo'), 'IANestingKey').id;
      const token = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').token;
      const csrfToken = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').csrfToken;

      const response = await axios.get(
        `${urlValue}/studio/notification-files/by-key?key=${encodeURIComponent(fileUrl)}`,
        {
          headers: {
            user: id,
            'access-token': token,
            'X-CSRF-Token': csrfToken
          },
          withCredentials: true,
          responseType: 'blob'
        }
      );

      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      openAlert('#alert-ac', () => setAlert({ title: 'Error downloading file', visible: true }), 3000, () => setAlert({ visible: false }));
    }
  };

  const uploadFile = async (notificationId) => {
    if (!selectedFile) return null;

    console.log('uploadFile called with notificationId:', notificationId);
    console.log('selectedFile:', selectedFile);

    const id = unfolder(localStorage.getItem('hashInfo'), 'IANestingKey').id;
    const token = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').token;
    const csrfToken = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').csrfToken;

    const formDataFile = new FormData();
    formDataFile.append('file', selectedFile);

    try {
      console.log('Sending file to server...');
      const res = await axios.post(`${urlValue}/studio/notification-files`, formDataFile, {
        headers: { 
          user: id, 
          'access-token': token,
          'X-CSRF-Token': csrfToken,
          'notification-id': notificationId,
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });
      console.log('Server response:', res.data);
      return res.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const id = unfolder(localStorage.getItem('hashInfo'), 'IANestingKey').id;
    const token = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').token;
    const csrfToken = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').csrfToken

    setLoading(true);
    try {
      let fileData = null;
      
      if (editId) {
        if (selectedFile) {
          fileData = await uploadFile(editId);
        }

        await axios.put(`${urlValue}/studio/release-notification/${editId}`, {
          ...formData,
          file_url: fileData ? fileData.url : uploadedFileUrl,
          file_name: fileData ? fileData.fileName : uploadedFileName
        }, {
          headers: { 
            user: id, 
            'access-token': token,
            'X-CSRF-Token': csrfToken
          },
          withCredentials: true
        });
      } else {

        console.log('Creating notification...', formData);
        const response = await axios.post(`${urlValue}/studio/release-notification`, formData, {
          headers: { 
            user: id, 
            'access-token': token,
            'X-CSRF-Token': csrfToken
          },
          withCredentials: true,
        });
        
        console.log('Notification created:', response.data);
        const newNotificationId = response.data.id;

        if (selectedFile && newNotificationId) {
          console.log('Uploading file for notification ID:', newNotificationId);
          fileData = await uploadFile(newNotificationId);
          console.log('File uploaded:', fileData);

          if (fileData && fileData.url) {
            console.log('Updating notification with file URL...');
            await axios.put(`${urlValue}/studio/release-notification/${newNotificationId}`, {
              ...formData,
              file_url: fileData.url,
              file_name: fileData.fileName
            }, {
              headers: { 
                user: id, 
                'access-token': token,
                'X-CSRF-Token': csrfToken
              },
              withCredentials: true
            });
            console.log('Notification updated with file URL');
          }
        } else if (selectedFile) {
          console.log('Warning: File selected but no notification ID');
        }
      }
      
      setFormData({ title: '', description: '', date: '' });
      setSelectedFile(null);
      setUploadedFileUrl('');
      setUploadedFileName('');
      setEditId(null);
      setRefresh(!refresh);
      openAlert('#alert-ac', () => setAlert({ title: t('alerts.notificationSaved'), visible: true }), 3000, () => setAlert({ visible: false }));
    } catch (error) {
      console.error(error);
      openAlert('#alert-ac', () => setAlert({ title: 'Error saving notification', visible: true }), 3000, () => setAlert({ visible: false }));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (notification) => {
    setFormData({
      title: notification.title,
      description: notification.description,
      date: notification.date?.slice(0, 10) || ''
    });
    setUploadedFileUrl(notification.file_url || '');
    setUploadedFileName(notification.file_name || '');
    setEditId(notification.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) return;
    const token = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').token;
    const csrfToken = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').csrfToken;
    setLoading(true);
    try {
      await axios.delete(`${urlValue}/studio/release-notification/${id}`, {
        headers: { 
          'access-token': token,
          'X-CSRF-Token': csrfToken
        },
        withCredentials: true
      });
      setRefresh(!refresh);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { header: t('common.title'), field: 'title', width: '25%' },
    { header: t('common.description'), field: 'description', width: '40%' },
    { header: t('common.date'), field: 'date', width: '10%' },
    { 
      header: 'File', 
      field: 'file_name', 
      width: '15%',
      render: (_, item) => (
        item.file_url ? (
          <button
            onClick={() => downloadFile(item.file_url, item.file_name)}
            style={{ 
              color: '#0038A8', 
              textDecoration: 'underline',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              font: 'inherit'
            }}
          >
            {item.file_name || 'Download'}
          </button>
        ) : (
          <span style={{ color: '#999' }}>No file</span>
        )
      )
    },
    {
      header: t('common.actions'),
      field: 'actions',
      width: '10%',
      align: 'center',
      render: (_, item) => (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
          <img src="/icons/edit.png" alt={t('common.edit')} style={{ width: 15, cursor: 'pointer' }} onClick={() => handleEdit(item)} />
          <img src="/icons/trash.png" alt={t('common.delete')} style={{ width: 15, cursor: 'pointer' }} onClick={() => handleDelete(item.id)} />
        </div>
      )
    }
  ];

  return (
    <div className='general-container'>
      <TitleBar />
      <div className='primary-container d-flex flex-row flex-wrap justify-content-between mt-3'>
        <h1>{t('menu.releaseNotifications')}</h1>
        <div className="box-tool">
          <h2>{editId ? t('menu.editNotification') : t('menu.newNotification')}</h2>
          <form onSubmit={handleSubmit} className='d-flex flex-row flex-wrap'>
            <div className='items-little'>
              <div className='input-container'>
                <label>{t('common.title')}:</label>
                <textarea name="title" value={formData.title} onChange={handleChange} required rows="2" />
              </div>
            </div>
            <div className='items-little'>
              <div className='input-container'>
                <label>{t('common.description')}:</label>
                <textarea name="description" value={formData.description} onChange={handleChange} required rows="2" />
              </div>
            </div>
            <div className='items-little'>
              <div className='input-container'>
                <label>{t('common.date')}:</label>
                <input type="date" name="date" value={formData.date} onChange={handleChange} required style={{ width: '100%', padding: '10px', borderRadius: '15px', border: '1px solid #cccccc' }} />
              </div>
            </div>
            <div className='items-little'>
              <div className='input-container'>
                <label>File (.docx or .mp4):</label>
                <input 
                  type="file" 
                  accept=".docx,.mp4" 
                  onChange={handleFileChange} 
                  style={{ width: '100%', padding: '10px', borderRadius: '15px', border: '1px solid #cccccc' }} 
                />
                {selectedFile && (
                  <p style={{ marginTop: '5px', fontSize: '12px', color: '#0038A8' }}>
                    Selected: {selectedFile.name}
                  </p>
                )}
                {!selectedFile && uploadedFileName && editId && (
                  <p style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                    Current: {uploadedFileName}
                  </p>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
              <button type="submit" style={{ padding: '8px 20px', borderRadius: 5, backgroundColor: '#0038A8', color: 'white', border: 'none' }}>
                {editId ? 'Update notification' : t('menu.createNotification')}
              </button>
              {editId && (
                <button type="button" onClick={() => { setEditId(null); setFormData({ title: '', description: '', date: '' }); setSelectedFile(null); setUploadedFileUrl(''); setUploadedFileName(''); }} style={{ marginLeft: 10, padding: '8px 20px', borderRadius: 5, backgroundColor: '#999', color: 'white', border: 'none' }}>
                  {t('common.cancel')}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
      <div className="primary-container mt-3">
        <DataTable
          columns={columns}
          data={notifications}
          keyField="id"
          emptyMessage={t('menu.noNotificationsFound')}
        />
      </div>
      {alert.visible && <Alerts id="alert-ac" title={alert.title} />}
    </div>
  );
};

export default ReleaseNotifications;